// Club-Profile Matches tab — integration scaffold (Build-Brief
// TP_BUILD_BRIEF_CLUB_MATCHES_TAB, §0.3 layout; handoff MatchesTabContent
// tokens: stack < 900, rail 304/344/392/456px by width, padX 16/24/32/40).
//
// Consumes MJ's /api/clubs/<public_id>/matches/ contract (#436/#438:
// club_side server-side, coverage.lineup, per-match competition ref incl.
// cur_season_id/kind) + CEO's components (StandingsStrip — deliberately
// selection-INDEPENDENT — ContextLine, builders). Rail + form strip are the
// functional in-scaffold build (boards C2/C5/C11); empty renders nothing,
// all labels translation keys, tabular-nums on numerals.
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import MatchLineup from "../player/MatchLineup/MatchLineup";
import ContextLine from "./ContextLine";
import StandingsStrip from "./StandingsStrip";
import { buildContextLine } from "./contextLineData";
import { buildCupPath, buildPointsExcerpt } from "./standingsStripData";
import { dashboardService } from "../../services/api";

const FINISHED = 8;

const useWidth = () => {
  const [w, setW] = useState(() => window.innerWidth);
  useEffect(() => {
    const on = () => setW(window.innerWidth);
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, []);
  return w;
};

const railWidth = (w) =>
  w < 1200 ? "304px" : w < 1600 ? "344px" : w < 2100 ? "392px" : "456px";
const padX = (w) => (w < 640 ? 16 : w < 1600 ? 24 : w < 2100 ? 32 : 40);

const fmtDate = (epoch, locale) =>
  epoch
    ? new Date(epoch * 1000).toLocaleDateString(locale, {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : null;

// W/D/L from the club's perspective (sides already normalised server-side)
const resultFor = (m) => {
  if (m.status_id !== FINISHED || !m.score) return null;
  const { winner, club_side: side } = m;
  if (!winner || winner === "draw") return "D";
  return winner === side ? "W" : "L";
};

const RESULT_STYLE = {
  W: { bg: "#18B249", fg: "#FFFFFF" },
  D: { bg: "#64748B", fg: "#FFFFFF" },
  L: { bg: "#D0162D", fg: "#FFFFFF" },
};

const scoreText = (m) => {
  if (!m.score || m.score.home == null) return null;
  let s = `${m.score.home}–${m.score.away}`;
  return s;
};

const suffixText = (m) => {
  if (!m.score) return "";
  if (m.score.penalty)
    return ` (${m.score.penalty.home}–${m.score.penalty.away} ${"pens"})`;
  if (m.score.aet) return ` ${"a.e.t."}`;
  return "";
};

// "2026-2027" -> "2026/27", plain "2026" stays; empty renders nothing
const fmtSeason = (year) =>
  year ? String(year).replace(/^(\d{4})-\d{2}(\d{2})$/, "$1/$2") : "";

const Crest = ({ logo, name }) =>
  logo ? (
    <img src={logo} alt={name || ""} className="w-5 h-5 object-contain shrink-0" />
  ) : null;

// ── left rail ────────────────────────────────────────────────────────────
const RailEntry = ({ m, selected, onSelect, locale }) => {
  const opp = m.club_side === "home" ? m.away : m.home;
  const res = resultFor(m);
  const score = scoreText(m);
  return (
    <button
      onClick={() => onSelect(m.id)}
      className={`w-full min-h-[44px] flex items-center gap-2 px-3 py-2 text-left rounded-lg ${
        selected ? "bg-[#F3F4F6]" : "hover:bg-[#FAFAFB]"
      }`}
    >
      <Crest logo={opp?.logo} name={opp?.name} />
      <span className="flex-1 truncate text-sm text-[#1A212F]">{opp?.name}</span>
      {score && (
        <span
          className="text-sm font-semibold"
          style={{ fontVariantNumeric: "tabular-nums" }}
        >
          {score}
          {/* C7: pens/OT suffixes also in the rail row, muted next to the score */}
          {suffixText(m) && (
            <span className="text-[11px] font-normal text-[#767A82]">
              {suffixText(m)}
            </span>
          )}
        </span>
      )}
      {res ? (
        <span
          className="w-6 h-6 rounded-md text-xs font-bold flex items-center justify-center"
          style={{ backgroundColor: RESULT_STYLE[res].bg, color: RESULT_STYLE[res].fg }}
        >
          {res}
        </span>
      ) : (
        <span className="text-xs text-[#767A82]" style={{ fontVariantNumeric: "tabular-nums" }}>
          {m.tbd ? "TBD" : fmtDate(m.match_time, locale)}
        </span>
      )}
    </button>
  );
};

const Rail = ({ upcoming, history, selectedId, onSelect, locale }) => {
  // history grouped by competition + season, order of appearance (payload is
  // desc) — the season label keeps the cross-season history readable when
  // older seasons are still in the 200-match window (Marin 2026-08-20)
  const groups = useMemo(() => {
    const map = new Map();
    history.forEach((m) => {
      const key = `${m.competition?.id || ""}|${m.season?.id || ""}`;
      if (!map.has(key))
        map.set(key, { competition: m.competition, season: m.season, matches: [] });
      map.get(key).matches.push(m);
    });
    return Array.from(map.values());
  }, [history]);

  return (
    <div className="flex flex-col gap-4">
      {upcoming.length > 0 && (
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-[#767A82] px-3 mb-1">
            {"Upcoming"}
          </p>
          {upcoming.slice(0, 2).map((m) => (
            <RailEntry
              key={m.id}
              m={m}
              selected={m.id === selectedId}
              onSelect={onSelect}
                           locale={locale}
            />
          ))}
        </div>
      )}
      {groups.map(
        (g) =>
          g.matches.length > 0 && (
            <div key={`${g.competition?.id || "other"}|${g.season?.id || ""}`}>
              <div className="flex items-center gap-2 px-3 mb-1">
                <Crest logo={g.competition?.logo} name={g.competition?.name} />
                <p className="text-xs font-semibold uppercase tracking-wide text-[#767A82] truncate">
                  {g.competition?.name}
                  {fmtSeason(g.season?.year) && (
                    <span
                      className="normal-case font-normal"
                      style={{ fontVariantNumeric: "tabular-nums" }}
                    >
                      {` · ${fmtSeason(g.season.year)}`}
                    </span>
                  )}
                </p>
              </div>
              {g.matches.map((m) => (
                <RailEntry
                  key={m.id}
                  m={m}
                  selected={m.id === selectedId}
                  onSelect={onSelect}
                                   locale={locale}
                />
              ))}
            </div>
          )
      )}
    </div>
  );
};

// ── form strip (last 5 played, cross-competition, newest right) ──────────
const FormStrip = ({ history, onSelect, locale }) => {
  const played = useMemo(
    () => history.filter((m) => resultFor(m)).slice(0, 5).reverse(),
    [history]
  );
  if (played.length === 0) return null;
  const wins = played.filter((m) => resultFor(m) === "W").length;
  const draws = played.filter((m) => resultFor(m) === "D").length;
  const losses = played.filter((m) => resultFor(m) === "L").length;
  const gf = played.reduce(
    (a, m) => a + (m.club_side === "home" ? m.score.home : m.score.away),
    0
  );
  const ga = played.reduce(
    (a, m) => a + (m.club_side === "home" ? m.score.away : m.score.home),
    0
  );
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wide text-[#767A82] mb-2">
        {"Form · last 5"}
      </p>
      <div className="flex items-end gap-2">
        {played.map((m) => {
          const res = resultFor(m);
          const opp = m.club_side === "home" ? m.away : m.home;
          return (
            <button
              key={m.id}
              onClick={() => onSelect(m.id)}
              title={`${fmtDate(m.match_time, locale)} · ${m.competition?.short_name || m.competition?.name || ""}${suffixText(m)}`}
              className="flex flex-col items-center gap-1 min-w-[44px]"
            >
              <span
                className="relative w-9 h-9 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: RESULT_STYLE[res].bg }}
              >
                <Crest logo={opp?.logo} name={opp?.name} />
                {m.competition?.logo && (
                  <img
                    src={m.competition.logo}
                    alt=""
                    className="absolute -top-1 -right-1 w-3.5 h-3.5 object-contain"
                  />
                )}
              </span>
              <span
                className="text-[11px] text-[#1A212F]"
                style={{ fontVariantNumeric: "tabular-nums" }}
              >
                {scoreText(m)}
              </span>
            </button>
          );
        })}
      </div>
      <p className="text-xs text-[#767A82] mt-1" style={{ fontVariantNumeric: "tabular-nums" }}>
        {`${wins} wins · ${draws} draws · ${losses} losses · ${gf}:${ga}`}
      </p>
    </div>
  );
};

// ── skeleton / error (C9 / C10) ──────────────────────────────────────────
const Skeleton = () => (
  <div className="animate-pulse flex flex-col gap-3 p-4">
    {[...Array(6)].map((_, i) => (
      <div key={i} className="h-10 bg-[#F3F4F6] rounded-lg" />
    ))}
  </div>
);

const ErrorState = ({ onRetry }) => (
  <div className="p-8 text-center">
    <p className="text-sm text-[#1A212F] mb-3">{"Could not load matches"}</p>
    <button
      onClick={onRetry}
      className="px-4 py-2 rounded-lg bg-[#F3F4F6] text-sm font-medium"
    >
      {"Retry"}
    </button>
  </div>
);

// ── container ────────────────────────────────────────────────────────────
const ClubMatchesTab = ({ clubId }) => {
  const navigate = useNavigate();
  const w = useWidth();
  const stack = w < 900;
  const locale = "en";

  const [data, setData] = useState(null);
  const [stripCards, setStripCards] = useState([]);
  const [status, setStatus] = useState("loading"); // loading | ready | error
  const [selectedId, setSelectedId] = useState(null);

  const load = useCallback(async () => {
    setStatus("loading");
    const res = await dashboardService.getClubMatches(clubId);
    if (!res.success) {
      setStatus("error");
      return;
    }
    setData(res.data);
    const hist = res.data.history || [];
    const up = res.data.upcoming || [];
    setSelectedId(hist[0]?.id || up[0]?.id || null);
    setStatus("ready");

    // standings strip: club context, match-independent (brief §0.3)
    const comps = new Map();
    [...up, ...hist].forEach((m) => {
      if (m.competition?.id && !comps.has(m.competition.id))
        comps.set(m.competition.id, m.competition);
    });
    const cards = [];
    for (const comp of comps.values()) {
      try {
        if (comp.kind === "cup") {
          const cupRes = await dashboardService.getCupBracket(comp.id);
          const cupPath = cupRes?.success && buildCupPath(cupRes.data, res.data.club?.row_key);
          if (cupPath) {
            cards.push({ key: comp.id, name: comp.name, logo: comp.logo, isKo: true, cupPath });
          } else if (comp.cur_season_id) {
            // no KO path yet (e.g. CL/EL league phase): fall back to the
            // standings excerpt when the phase has a table (Marin 2026-08-20)
            const stRes = await dashboardService.getSeasonStandings(comp.cur_season_id);
            const rows = stRes?.success && buildPointsExcerpt(stRes.data?.standings || stRes.data, res.data.club?.row_key);
            if (rows && rows.length)
              cards.push({ key: comp.id, name: comp.name, logo: comp.logo, isPoints: true, rows });
          }
        } else if (comp.kind === "league" && comp.cur_season_id) {
          // leagues only: friendlies ("other") carry provider pseudo-seasons
          // with pseudo-standings — no card for them (brief §0.3 knows only
          // points/KO cards of real competitions; Marin + CEO finding)
          const stRes = await dashboardService.getSeasonStandings(comp.cur_season_id);
          const rows = stRes?.success && buildPointsExcerpt(stRes.data?.standings || stRes.data, res.data.club?.row_key);
          if (rows && rows.length)
            cards.push({ key: comp.id, name: comp.name, logo: comp.logo, isPoints: true, rows });
        }
      } catch (e) {
        // a single failed card never breaks the strip
      }
    }
    setStripCards(cards);
  }, [clubId]);

  useEffect(() => {
    load();
  }, [load]);

  if (status === "loading") return <Skeleton />;
  if (status === "error") return <ErrorState onRetry={load} />;

  const all = [...(data.upcoming || []), ...(data.history || [])];
  const selected = all.find((m) => m.id === selectedId) || null;
  const showLineup =
    selected && selected.coverage?.lineup === 1 && selected.status_id === FINISHED;

  const onTeamClick = (teamId) => teamId && navigate(`/search/club/${teamId}`);
  // card header -> competition page (full table / bracket); the card itself
  // stays the compact excerpt (brief §0.3 — strip never pushes the detail
  // below the fold; Marins scroll question 2026-08-20)
  const onCompetitionClick = (compId) =>
    compId && navigate(`/competitions/${compId}`);

  // the strip renders exactly ONCE per layout (§3 finding: the stack branch
  // used to mount it here AND before the rail — two identical blocks <900px)
  const strip = stripCards.length > 0 && (
    <StandingsStrip
      cards={stripCards}
      stack={stack}
      wide={w >= 1600}
      onTeamClick={onTeamClick}
      onCompetitionClick={onCompetitionClick}
    />
  );

  const detail = selected && (
    <div className="flex flex-col gap-4">
      <ContextLine ctx={buildContextLine(selected)} onTeamClick={onTeamClick} />
      <FormStrip history={data.history || []} onSelect={setSelectedId} locale={locale} />
      {showLineup ? (
        <MatchLineup matchId={selected.id} />
      ) : (
        <p className="text-sm text-[#767A82] py-6 text-center">
          {"Lineup not available for this match"}
        </p>
      )}
    </div>
  );

  if (stack) {
    // C11 order verbatim: Upcoming → standings swipe → history rail → detail
    // (Rail renders nothing for an empty section, so the split costs nothing)
    return (
      <div style={{ padding: `16px ${padX(w)}px` }} className="flex flex-col gap-4">
        <Rail
          upcoming={data.upcoming || []}
          history={[]}
          selectedId={selectedId}
          onSelect={setSelectedId}
          locale={locale}
        />
        {strip}
        <Rail
          upcoming={[]}
          history={data.history || []}
          selectedId={selectedId}
          onSelect={setSelectedId}
          locale={locale}
        />
        {detail}
      </div>
    );
  }

  const rail = (
    <Rail
      upcoming={data.upcoming || []}
      history={data.history || []}
      selectedId={selectedId}
      onSelect={setSelectedId}
      locale={locale}
    />
  );

  return (
    <div
      style={{
        padding: `20px ${padX(w)}px`,
        display: "grid",
        gridTemplateColumns: `${railWidth(w)} minmax(0, 1fr)`,
        gap: w < 1600 ? 22 : 30,
        alignItems: "start",
      }}
    >
      <div className="border-r border-[#E5E6E8] pr-3">{rail}</div>
      <div className="flex flex-col gap-4">
        {strip}
        {detail}
      </div>
    </div>
  );
};

export default ClubMatchesTab;
